import React, { useState, useEffect, useRef } from "react";
import "./evci-filters.css";
import filterJsonData from "./evci-filters.json";

// Custom Dropdown Component
const CustomDropdown = ({ id, className, label, value, onChange, disabled, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('Select...');
  const dropdownRef = useRef(null);

  // Extract options from children
  const options = React.Children.map(children, child => {
    if (child && child.props) {
      return {
        label: child.props.label || child.props.children,
        value: child.props.value
      };
    }
    return null;
  }).filter(Boolean);

  // Update selected label when value changes
  useEffect(() => {
    const selectedOption = options.find(opt => opt.value === value);
    if (selectedOption) {
      setSelectedLabel(selectedOption.label);
    } else {
      // Show label with colon when no value is selected
      setSelectedLabel(`${label} : Select...`);
    }
  }, [value, options, label]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOptionClick = (optionValue) => {
    if (!disabled) {
      onChange(optionValue);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className={className} ref={dropdownRef}>
      <div className="custom-dropdown-container">
        <button
          id={id}
          type="button"
          className={`custom-dropdown-button ${disabled ? 'disabled' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="custom-dropdown-text">{selectedLabel}</span>
          <span className="custom-dropdown-arrow">▼</span>
        </button>

        {isOpen && !disabled && (
          <div className="custom-dropdown-menu">
            {options.map((option, index) => (
              <div
                key={option.value || index}
                className={`custom-dropdown-option ${option.value === value ? 'selected' : ''}`}
                onClick={() => handleOptionClick(option.value)}
                role="option"
                aria-selected={option.value === value}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DropdownOption = ({ label, value }) => null; // This is just for compatibility

function EVCIFilters({ filters, onFilterChange, lastRefreshTime }) {
  // State to force re-render for time updates
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute to refresh the "mins ago" display
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Ensure all filters have default empty string if not provided
  const defaultFilters = {
    Country: '',
    State: '',
    Site: '',
    OnelineDiagram: '',
    RefreshRate: ''
  };
  filters = { ...defaultFilters, ...filters };

  // Helper to get options at each level
  const getCountryOptions = () =>
    Object.keys(filterJsonData)
      .filter(k => k !== 'RefreshRate')
      .map(country => ({ label: country, value: country }));

  const getStateOptions = country =>
    country && filterJsonData[country]
      ? Object.keys(filterJsonData[country]).map(state => ({ label: state, value: state }))
      : [];

  const getSiteOptions = (country, state) =>
    country && state && filterJsonData[country]?.[state]
      ? Object.keys(filterJsonData[country][state]).map(site => ({ label: site, value: site }))
      : [];

  const getDiagramOptions = (country, state, site) =>
    country && state && site && filterJsonData[country]?.[state]?.[site]
      ? Object.keys(filterJsonData[country][state][site]).map(diagram => ({ label: diagram, value: diagram }))
      : [];

  const getRefreshRateOptions = () =>
    filterJsonData.RefreshRate ? filterJsonData.RefreshRate.options : [];

  const dropdownKeys = ['Country', 'State', 'Site', 'OnelineDiagram', 'RefreshRate'];

  // Handle cascading reset
  const handleChange = key => value => {
    console.log(`Changing ${key} to ${value}`);
    let newFilters = { ...filters, [key]: value };

    if (key === 'Country') {
      newFilters.State = '';
      newFilters.Site = '';
      newFilters.OnelineDiagram = '';
      console.log('Country changed - new filters:', newFilters);
    } else if (key === 'State') {
      newFilters.Site = '';
      newFilters.OnelineDiagram = '';
      console.log('State changed - new filters:', newFilters);
    } else if (key === 'Site') {
      newFilters.OnelineDiagram = '';
      console.log('Site changed - new filters:', newFilters);
    } else if (key === 'OnelineDiagram') {
      console.log('OnelineDiagram changed - new filters:', newFilters);
    }

    // If all five are selected, print the full selection and the list as a single object
    if (
      newFilters.Country &&
      newFilters.State &&
      newFilters.Site &&
      newFilters.OnelineDiagram &&
      newFilters.RefreshRate
    ) {
      const list =
        filterJsonData?.[newFilters.Country]?.[newFilters.State]?.[newFilters.Site]?.[newFilters.OnelineDiagram]?.list || [];
      // const result = {
      //   ...newFilters,
      //   list
      // };
      console.log("All filters selected:", newFilters, "List:", list);
      newFilters = { ...newFilters, list }; // Add the list to the filters
    }

    onFilterChange(newFilters);
  };

  // Dropdown rendering
  const renderDropdown = key => {
    let options = [];
    let value = filters[key] || '';
    let isDisabled = false;

    switch (key) {
      case 'Country':
        options = getCountryOptions();
        isDisabled = false; // Always enabled
        break;
      case 'State':
        options = getStateOptions(filters.Country);
        isDisabled = !filters.Country; // Only enabled if country selected
        break;
      case 'Site':
        options = getSiteOptions(filters.Country, filters.State);
        isDisabled = !filters.Country || !filters.State; // Only enabled if country and state selected
        break;
      case 'OnelineDiagram':
        options = getDiagramOptions(filters.Country, filters.State, filters.Site);
        isDisabled = !filters.Country || !filters.State || !filters.Site; // Only enabled if all parents selected
        break;
      case 'RefreshRate':
        options = getRefreshRateOptions();
        isDisabled = false; // Always enabled
        break;
      default:
        break;
    }

    // Always show Select... as first option
    return (
      <CustomDropdown
        key={key}
        id={key}
        className="evci-filter-dropdown"
        label={key}
        value={value}
        onChange={handleChange(key)}
        disabled={isDisabled}
      >
        <DropdownOption key="" label="Select..." value="" />
        {options.map(option => (
          <DropdownOption key={option.value} label={option.label} value={option.value} />
        ))}
      </CustomDropdown>
    );
  };

  // Helper function to calculate time difference
  const getTimeDifference = (lastRefresh) => {
    if (!lastRefresh) return '';

    const diffMs = currentTime - lastRefresh;
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'just now';
    if (diffMins === 1) return '1 min ago';
    return `${diffMins} mins ago`;
  };

  return (
    <div className="Rim-filter-row">
      <div className="Rim-filter-header">
        <span>One line Diagram</span>
      </div>
      {lastRefreshTime && (
        <div className="last-refresh-container">
          <span className="last-refresh">
            Last refresh on : {getTimeDifference(lastRefreshTime)}
          </span>
        </div>
      )}
      <div className="Rim-filters-group">
        {dropdownKeys.map(renderDropdown)}
      </div>
    </div>
  );
}

export default EVCIFilters;